/**
 * @fileoverview Confirmation Modal Component
 * 
 * This file contains a reusable confirmation modal component for the Heidrick & Struggles
 * Leader Profile application. The modal provides a consistent way to ask for user
 * confirmation before performing important actions.
 * 
 * Features:
 * - Customizable header and body text
 * - Optional cancel button
 * - Loading state for the continue button
 * - Standardized styling consistent with application design
 */

import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalSize, Table , Tabs, Tab, TabSize, TabVariant, Button, ButtonVariant, IconName, ButtonIconAlign } from '@eightfold.ai/octuple';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Api from '../Api';
import { globalContext } from '../store';
import { useTableSort } from '../commons/commonFunctions'
import { actionButtonStyle } from './commonStyles';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Format a date string using dayjs with MMM D, YYYY format
 * @param {string|Date} dateString - The date string or Date object to format
 * @returns {string} Formatted date string
 */
const formatDate = (dateString: any) => {
  if (!dateString) return '';
  return dayjs(dateString).format('MMM D, YYYY');
};

/**
 * Custom hook to detect screen size and determine if we're in mobile view
 * @returns {boolean} True if screen width is between 321px and 600px
 */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth >= 321 && window.innerWidth <= 600);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

/**
 * Card View Component for Mobile
 * 
 * Renders data in card format for better mobile experience
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - The data to display in card format
 * @returns {JSX.Element} The card layout
 */
const CardView = ({ data }: { data: any[] }) => {
  if (!data || !Array.isArray(data)) return null;

  return (
    <div className="completedAssessmentsCardContainer">
      {data.map((item:any, index:any) => (
        <>
          <div className="completedAssessmentCardItem">
              <h3>{item?.title}</h3>
              <div className="completedAssessmentDetailsRow">
                <span className='completedAssessmentTextStyle'>{formatDate(item?.completedDate)}</span>
                <span className='completedAssessmentTextStyle'>{item?.assessmentType}</span>
              </div>
          </div>
          <div className="completedAssessmentItemDivider"></div>
        </>
      ))}
    </div>
  );
};

/**
 * Document Card View Component for Mobile
 * 
 * Renders documents in card format for better mobile experience with download button
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - The documents data to display
 * @param {Function} props.onDownload - Handler for download action
 * @param {string|null} props.downloadingId - Currently downloading attachment ID
 * @returns {JSX.Element} The card layout
 */
const DocumentCardView = ({ data, onDownload, downloadingId }: { 
  data: any[]; 
  onDownload: (attachmentId: string, reportName: string) => void;
  downloadingId: string | null;
}) => {
  if (!data || !Array.isArray(data)) return null;

  return (
    <div className="completedAssessmentsCardContainer">
      {data.map((item: any, index: any) => (
        <div key={item?.id || index}>
          <div className="completedAssessmentCardItem">
            <h3>{item?.reportName || item?.name}</h3>
            <div className="completedAssessmentDetailsRow">
              <span className='completedAssessmentTextStyle'>{formatDate(item?.reportDate)}</span>
            </div>
            <div style={{ marginTop: '12px' }}>
              <Button
                text='Download PDF'
                variant={ButtonVariant.Secondary}
                classNames='actionButton'
                style={actionButtonStyle}
                onClick={() => onDownload(item?.id, item?.reportName || item?.name)}
                iconProps={downloadingId === item?.id 
                  ? { path: IconName.mdiLoading, spin: true } 
                  : { path: IconName.mdiArrowCollapseDown }}
                alignIcon={ButtonIconAlign.Right}
                disabled={downloadingId === item?.id}
              />
            </div>
          </div>
          <div className="completedAssessmentItemDivider"></div>
        </div>
      ))}
    </div>
  );
};

/**
 * Modal Body Component
 * 
 * Renders the content of the confirmation modal including message text
 * and action buttons (Cancel and Continue).
 * 
 * @param {Object} props - Component props
 * @param {string} props.bodyText - The message text to display in the modal
 * @param {boolean} props.showCancelButton - Whether to show the Cancel button
 * @param {boolean} props.continueButtonLoader - Whether to show a loading spinner on the Continue button
 * @param {Function} props.onClose - Function to call when the modal is closed/cancelled
 * @param {Function} props.onContinue - Function to call when the Continue button is clicked
 * @param {number} props.closedAssessCount - The count of closed assessments
 * @param {number} props.completedAssessCount - The count of completed assessments
 * @returns {JSX.Element} The modal body content
 */
/**
 * Custom title component with sort icon
 * Returns a title with appropriate sort icon
 * 
 * @param {string} title - The column title text
 * @param {string} sortOrder - The current sort direction ('ascend', 'descend', or null)
 * @returns {JSX.Element} The title with sort icon
 */
const TitleWithSortIcon = ({ title, sortOrder }: { title: string; sortOrder?: string | null }) => {
  const getSortIcon = () => {
    if (sortOrder === 'ascend') {
      return (
        <img
          src="/assets/img/arrows-up.svg"
          alt="Sort Ascending"
          className="sort-icon"
        />
      );
    } else if (sortOrder === 'descend') {
      return (
        <img
          src="/assets/img/arrows-down.svg"
          alt="Sort Descending"
          className="sort-icon"
          style={{
            transform: 'rotate(0deg)',
          }}
        />
      );
    } else {
      return (
        <img
          src="/assets/img/arrows-vertical.svg"
          alt="Sort"
          className='sort-icon'
        />
      );
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {title}
      {getSortIcon()}
    </div>
  );
};

const ModalBody = (props: any) => {
  const isMobile = useIsMobile();
  const { setSortInfo, handleTableChange, getSortOrderForColumn } = useTableSort(
    props?.activeTab === 'Completed Assessments' ? 'completedDate' : 'dueDate',
    'descend'
  );

  // Update sortInfo when activeTab changes
  useEffect(() => {
    setSortInfo({
      field: props?.activeTab === 'Completed Assessments' ? 'completedDate' : 'dueDate',
      order: 'descend'
    });
  }, [props?.activeTab, setSortInfo]);

  const columns:any = [
    {
      dataIndex: 'title',
      classNames: 'tableColumnTitle',
      sortDirections: [
        'ascend',
        'descend'
      ],
      sorter: {
        compare: (a: any, b: any) => a.title.localeCompare(b.title)
      },
      title: <TitleWithSortIcon 
        title="Name" 
        sortOrder={getSortOrderForColumn('title')} 
      />
    },
    {
      dataIndex: props?.activeTab === 'Completed Assessments' ? 'completedDate' : 'dueDate',
      classNames: 'completedAssessmentTextStyle',
      render: (text: any) => formatDate(text),
      sortDirections: [
        'ascend',
        'descend'
      ],
      defaultSortOrder: 'descend',
      sorter: {
        compare: (a:any, b:any) => new Date(props?.activeTab === 'Completed Assessments' ? a.completedDate : a.dueDate).getTime() - new Date(props?.activeTab === 'Completed Assessments' ? b.completedDate : b.dueDate).getTime()
      },
      title: <TitleWithSortIcon 
        title= {props?.activeTab === 'Completed Assessments' ?"Completed Date" : "Due Date"} 
        sortOrder={getSortOrderForColumn(props?.activeTab === 'Completed Assessments' ? 'completedDate' : 'dueDate')} 
      />
    },
    {
      dataIndex: 'assessmentType',
      classNames: 'completedAssessmentTextStyle',
      sortDirections: [
        'ascend',
        'descend'
      ],
      sorter: {
        compare: (a:any, b:any) => a.assessmentType.localeCompare(b.assessmentType)
      },
      title: <TitleWithSortIcon 
        title="Assessment Type" 
        sortOrder={getSortOrderForColumn('assessmentType')} 
      />
    }
  ];

  return (
    <>
      {isMobile ? (
        <CardView data={props?.data} />
      ) :(
        <div className='completedAssessmentContainer' style={{ padding: isMobile ? '16px' :'' }}>
          <Table
            bordered
            classNames="completedAssessmentsTable"
            columns={columns}
            dataSource={props?.data}
            headerClassName="my-header"
            id="myTableId"
            pagination={false}
            onChange={handleTableChange}
            alternateRowColor={false}
            rowHoverBackgroundEnabled={false}
          />
        </div>
      )}
    </>
  );
};

/**
 * Documents Body Component
 * 
 * Renders the documents table with download functionality
 * 
 * @param {Object} props - Component props
 * @param {Array} props.documentsData - The documents to display
 * @returns {JSX.Element} The documents table content
 */
const DocumentsBody = (props: any) => {
  const isMobile = useIsMobile();
  const { dispatch } = useContext(globalContext);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { handleTableChange, getSortOrderForColumn } = useTableSort('reportDate', 'descend');

  /**
   * Handles document download
   * @param attachmentId - The ID of the attachment to download
   * @param reportName - The name of the report for the downloaded file
   */
  const handleDownload = async (attachmentId: string, reportName: string) => {
    if (!attachmentId) return;
    setDownloadingId(attachmentId);
    try {
      const result = await Api.downloadLeaderReports(attachmentId, reportName);
      if (!result.success) {
        // Error is handled by Api.downloadLeaderReports via snackbar
      }
    } catch (err: any) {
      if (err?.code === 401 && err?.message === 'Unauthorized Access') {
        dispatch({ type: 'SET_SHOW_LOGOUT_MODAL', payload: true });
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const documentColumns: any = [
    {
      dataIndex: 'reportName',
      classNames: 'tableColumnTitle',
      sortDirections: ['ascend', 'descend'],
      sorter: {
        compare: (a: any, b: any) => (a.reportName || a.name || '').localeCompare(b.reportName || b.name || '')
      },
      title: <TitleWithSortIcon 
        title="Document Name" 
        sortOrder={getSortOrderForColumn('reportName')} 
      />,
      render: (text: any, record: any) => record?.reportName || record?.name || ''
    },
    {
      dataIndex: 'reportDate',
      classNames: 'completedAssessmentTextStyle',
      render: (text: any) => formatDate(text),
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'descend',
      sorter: {
        compare: (a: any, b: any) => new Date(a.reportDate).getTime() - new Date(b.reportDate).getTime()
      },
      title: <TitleWithSortIcon 
        title="Date" 
        sortOrder={getSortOrderForColumn('reportDate')} 
      />
    },
    {
      dataIndex: 'action',
      classNames: 'completedAssessmentTextStyle',
      title: 'Action',
      render: (_: any, record: any) => (
        <Button
          text='Download PDF'
          variant={ButtonVariant.Secondary}
          classNames='actionButton'
          style={actionButtonStyle}
          onClick={() => handleDownload(record?.id, record?.reportName || record?.name)}
          iconProps={downloadingId === record?.id 
            ? { path: IconName.mdiLoading, spin: true } 
            : { path: IconName.mdiArrowCollapseDown }}
          alignIcon={ButtonIconAlign.Right}
          disabled={downloadingId === record?.id}
        />
      )
    }
  ];

  // Empty state when no documents
  if (!props?.documentsData || props?.documentsData?.length === 0) {
    return (
      <div className='completedAssessmentContainer' style={{ padding: '24px', textAlign: 'center' }}>
        <p style={{ color: '#666', fontSize: '14px' }}>No documents available</p>
      </div>
    );
  }

  return (
    <>
      {isMobile ? (
        <DocumentCardView 
          data={props?.documentsData} 
          onDownload={handleDownload}
          downloadingId={downloadingId}
        />
      ) : (
        <div className='completedAssessmentContainer'>
          <Table
            bordered
            classNames="completedAssessmentsTable"
            columns={documentColumns}
            dataSource={props?.documentsData}
            headerClassName="my-header"
            id="documentsTableId"
            pagination={false}
            onChange={handleTableChange}
            alternateRowColor={false}
            rowHoverBackgroundEnabled={false}
          />
        </div>
      )}
    </>
  );
};

/**
 * Confirmation Modal Component
 * 
 * A reusable modal dialog that prompts users to confirm or cancel an action.
 * This component encapsulates the Modal from Octuple UI library and provides
 * a simplified interface for showing confirmation dialogs throughout the application.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.showModal - Whether the modal is visible
 * @param {string} props.header - The header text for the modal
 * @param {string} props.bodyText - The message text to display in the modal body
 * @param {boolean} props.showCancelButton - Whether to show a Cancel button (optional)
 * @param {boolean} props.continueButtonLoader - Whether the Continue button should show a loading state
 * @param {Function} props.onClose - Function to call when the modal is closed or cancelled
 * @param {Function} props.onContinue - Function to call when the Continue button is clicked
 * @param {number} props.closedAssessCount - The count of closed assessments
 * @param {number} props.completedAssessCount - The count of completed assessments
 * @returns {JSX.Element} The confirmation modal component
 */

const Header = ({ header, closedAssessCount, completedAssessCount, documentsCount, activeTab, onTabChange }: { 
  header?: string, 
  closedAssessCount: number, 
  completedAssessCount: number,
  documentsCount: number,
  activeTab: string,
  onTabChange: (tab: string) => void
}) => {
  const [firstLine, secondLine] = header ? header.split('\n') : ['Completed Assessments', ''];
  return (
    <div>
      <span className="completedModalHeaderStyle">{firstLine}</span>
      <div className="completedModalHeaderStyle" style={{ fontWeight:800, marginTop:'10px'}}>{secondLine}</div>
        <div className='filterButtonWrap' >
          <Tabs
              configContextProps={{
                  noThemeContext: false
              }}
              onChange={(tab) => onTabChange(tab)}
              value={activeTab}
              size={TabSize.Medium}
              themeContainerId="my-tabs-theme-container"
              variant={TabVariant.pill}
              style={{flexDirection:'row', width: '100%'}}
          >
            <Tab
              ariaLabel="Completed Assessments"
              label={`Completed Assessments (${completedAssessCount})`}
              value="Completed Assessments"
            />
            <Tab
              ariaLabel="Closed Assessments"
              label={`Closed Assessments (${closedAssessCount})`}
              value="Closed Assessments"
            />
            <Tab
              ariaLabel="Documents"
              label={`Documents (${documentsCount})`}
              value="Documents"
            />
          </Tabs>
        </div>
    </div>
  );
};

const CompletedAssessmentsTableModal = (props: any) => {
  const [activeTab, setActiveTab] = useState('Completed Assessments');

  /**
   * Handles the close/cancel action
   * Calls the onClose callback provided in props
   */
  const onClose = () => {
    props.onClose();
  };

  /**
   * Handles tab change and updates the active tab state
   */
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  // Determine which data to display based on active tab
  const displayData = activeTab === 'Completed Assessments' ? props?.data : props?.closedAssessmentdata;

  const getHeaderText = () => {
    if (activeTab === 'Documents') {
      const count = props?.documentsCount || 0;
      const text = count === 1 ? 'document' : 'documents';
      return `You have ${count} ${text}.`;
    }
    const count = activeTab === 'Completed Assessments' ? props?.completedAssessCount : props?.closedAssessCount;
    const label = activeTab === 'Completed Assessments' ? 'completed assessment' : 'closed assessment';
    const text = count === 1 ? label : `${label}s`;
    return `You have ${count} ${text}.`;
  }

  /**
   * Renders the appropriate body content based on active tab
   */
  const renderBody = () => {
    if (activeTab === 'Documents') {
      return <DocumentsBody documentsData={props?.documentsData} />;
    }
    return (
      <ModalBody
        continueButtonLoader={props?.continueButtonLoader}
        showCancelButton={props?.showCancelButton}
        onClose={onClose}
        bodyText={props?.bodyText}
        subtitle={props?.subtitle}
        color={props?.color}
        data={displayData}
        activeTab={activeTab}
      />
    );
  };
  
  return (
    <Modal
      visible={props?.showModal}
      actionsClassNames='my-modal-actions-class'
      body={renderBody()}
      bodyClassNames='my-modal-body-class'
      bodyPadding
      configContextProps={{
        noGradientContext: false,
        noThemeContext: false,
      }}
      closeButtonProps={{style:{background:'#fff'}}}
      data-test-id='my-modal-test-id'
      header={
        <Header 
          header={getHeaderText()} 
          closedAssessCount={props?.closedAssessCount} 
          completedAssessCount={props?.completedAssessCount}
          documentsCount={props?.documentsCount || 0}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      }
      headerClassNames='my-modal-header-class'
      modalClassNames='completedAssessmentsModalStyle'
      modalWrapperClassNames='my-modal-wrapper-class'
      onClose={onClose}
      overlay
      positionStrategy='absolute'
      renderContentAlways
      size={ModalSize.large}
      themeContainerId='my-modal-theme-container'
      zIndex={1000}
    />
  );
};

export default CompletedAssessmentsTableModal;
